/**
 * DIGIPIN API access — uses shared resilient apiClient.
 */

import { apiJson } from './apiClient';
import { DIGIPIN_CLOUD_API_URL as CLOUD } from '../config/api';

export interface DigipinRequest {
  id: string;
  user_id?: string;
  use_case?: string;
  useCase?: string;
  organization?: string | null;
  estimated_monthly_calls?: number | null;
  status: string;
  admin_note?: string | null;
  reviewed_at?: string | null;
  created_at?: string;
  /** How many historical requests this user has (admin list, one row per user) */
  request_count?: number;
  user_email?: string;
}

export interface DigipinKeyMeta {
  id: string;
  user_id?: string;
  key_prefix: string;
  status: string;
  scopes?: string[];
  rate_limit_per_day?: number;
  last_used_at?: string | null;
  created_at?: string;
  revoked_at?: string | null;
  user_email?: string;
  has_one_time_key?: boolean;
  /** Total keys ever issued for this user (admin list, one row per user) */
  key_count?: number;
  revoked_count?: number;
}

export interface DigipinAdminStats {
  requests: Record<string, number>;
  keys: Record<string, number>;
  callsToday: number;
  pendingCount: number;
  activeKeys: number;
}

export async function fetchMyDigipinAccess() {
  return apiJson<{
    success: boolean;
    request: DigipinRequest | null;
    keys: DigipinKeyMeta[];
  }>('/api/digipin-access/me');
}

export async function claimDigipinApiKey() {
  return apiJson<{
    success: boolean;
    key: string;
    keyPrefix: string;
    keyId: string;
    warning?: string;
    message?: string;
  }>('/api/digipin-access/claim-key', {
    method: 'POST',
    retry: false,
  });
}

export async function requestDigipinAccess(body: {
  useCase: string;
  organization?: string;
  estimatedMonthlyCalls?: number;
}) {
  return apiJson('/api/digipin-access/request', {
    method: 'POST',
    body,
    retry: false,
  });
}

export async function adminListDigipinRequests(status?: string) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiJson<{
    success: boolean;
    requests: Array<DigipinRequest & { user_email?: string }>;
  }>(`/api/admin/digipin-access/requests${q}`);
}

export async function adminListDigipinKeys(status?: string) {
  const q = status ? `?status=${encodeURIComponent(status)}` : '';
  return apiJson<{ success: boolean; keys: DigipinKeyMeta[] }>(
    `/api/admin/digipin-access/keys${q}`
  );
}

export async function adminReviewDigipinRequest(
  id: string,
  body: { action: 'approve' | 'reject'; adminNote?: string }
) {
  return apiJson<{
    success: boolean;
    status: string;
    key?: string;
    keyPrefix?: string;
    warning?: string;
    userEmail?: string | null;
    emailSent?: boolean;
    emailMock?: boolean;
  }>(`/api/admin/digipin-access/requests/${id}/review`, {
    method: 'POST',
    body,
    retry: false,
  });
}

export async function adminRevokeDigipinKey(id: string) {
  return apiJson(`/api/admin/digipin-access/keys/${id}/revoke`, {
    method: 'POST',
    retry: false,
  });
}

export async function adminGetDigipinStats() {
  return apiJson<{ success: boolean; stats: DigipinAdminStats }>('/api/admin/digipin-access/stats');
}

export async function adminReissueDigipinKey(userId: string, body?: { adminNote?: string }) {
  return apiJson<{
    success: boolean;
    key?: string;
    keyPrefix?: string;
    warning?: string;
  }>(`/api/admin/digipin-access/users/${userId}/reissue`, {
    method: 'POST',
    body: body || {},
    retry: false,
  });
}

export const DIGIPIN_CLOUD_API_URL = CLOUD;
export const DEVELOPERS_DIGIPIN_PATH = '/developers/digipin';
